/**
 * Reactive outbox state hook (sub-commit 4b).
 *
 * Consumers (today: OfflineBanner) need to re-render whenever the
 * outbox changes — when a new chat-send is queued, when a retry
 * succeeds and the item is removed, when statuses transition.
 *
 * Implementation: useSyncExternalStore wrapping the pub/sub +
 * snapshot cache exposed by outbox.ts. The snapshot cache keys on
 * (userId, version) so React's Object.is bail-out works
 * correctly — calling this hook on every render is cheap.
 *
 * Scoping: pulls userId from Clerk's useAuth(). When userId flips
 * to null on sign-out, the snapshot cache returns an empty array
 * naturally (cache key mismatch). No explicit cleanup needed.
 *
 * Mirrors the connection.ts pub/sub + useConnection() consumer
 * pattern.
 */

import { useAuth } from '@clerk/clerk-react';
import { useSyncExternalStore } from 'react';
import {
  getOutboxSnapshot,
  subscribeOutbox,
  type OutboxItem,
} from './outbox';

// Stable reference for the server snapshot path (and for the
// signed-out state before the snapshot cache populates). Module-
// level so all consumers share the same empty array — multiple
// useOutbox calls during the same render get the same reference.
const EMPTY_ITEMS: readonly OutboxItem[] = Object.freeze([]);

export interface UseOutboxResult {
  items: readonly OutboxItem[];
  count: number;
}

export function useOutbox(): UseOutboxResult {
  const { userId } = useAuth();
  const items = useSyncExternalStore<readonly OutboxItem[]>(
    subscribeOutbox,
    () => getOutboxSnapshot(userId),
    () => EMPTY_ITEMS,
  );
  return { items, count: items.length };
}
